import type { SharedRoomEventType, SharedRoomSnapshot } from "@hanjan-shieo/domain";
import { channelApiUrl } from "./channel";

export type RoomParticipantView = SharedRoomSnapshot["participants"][number] & { nickname?:string };
export type RoomSnapshotView = Omit<SharedRoomSnapshot,"participants"> & { participants:RoomParticipantView[] };
export type RoomCredentials = { roomId:string; participantId:string; roomToken:string; role:"host"|"participant"; expiresAt:string };

type ApiSuccess<T> = { ok:true; data:T };
type ApiFailure = { ok:false; error?:{ code?:string; message?:string } };
const key = (roomId:string) => `hs-room-v1:${roomId.toUpperCase()}`;

class ApiRequestError extends Error {
  constructor(readonly status:number, readonly code:string|undefined, message:string) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function api<T>(path:string, init:RequestInit = {}) {
  const response = await fetch(channelApiUrl(path), init);
  const body = await response.json().catch(()=>null) as ApiSuccess<T>|ApiFailure|null;
  if (!response.ok || !body?.ok) throw new ApiRequestError(response.status, body && !body.ok ? body.error?.code : undefined, body && !body.ok ? body.error?.message ?? "방 요청을 완료하지 못했어요." : "방 요청을 완료하지 못했어요.");
  return body.data;
}

async function guestToken() {
  return api<{ token:string }>("/api/v1/sessions/guest", { method:"POST" });
}

export function saveRoomCredentials(storage:Pick<Storage,"setItem">, credentials:RoomCredentials) {
  storage.setItem(key(credentials.roomId), JSON.stringify(credentials));
}

export function loadRoomCredentials(storage:Pick<Storage,"getItem"|"removeItem">, roomId:string):RoomCredentials|null {
  try {
    const parsed = JSON.parse(storage.getItem(key(roomId)) ?? "null") as RoomCredentials|null;
    if (!parsed || parsed.roomId !== roomId.toUpperCase() || Date.parse(parsed.expiresAt) <= Date.now()) { storage.removeItem(key(roomId)); return null; }
    return parsed;
  } catch { storage.removeItem(key(roomId)); return null; }
}

export async function createRoom(partySize:number, catalogVersion:string) {
  const guest = await guestToken();
  const created = await api<{ roomId:string; code:string; roomToken:string; expiresAt:string }>("/api/v1/rooms", { method:"POST", headers:{ "content-type":"application/json", authorization:`Bearer ${guest.token}` }, body:JSON.stringify({ partySize, selectedProps:[], catalogVersion }) });
  const snapshot = await fetchRoomSnapshot(created.code, created.roomToken);
  const credentials:RoomCredentials = { roomId:created.code, participantId:snapshot.hostParticipantId, roomToken:created.roomToken, role:"host", expiresAt:created.expiresAt };
  return { credentials, snapshot };
}

export async function joinRoom(code:string, nickname:string) {
  const guest = await guestToken();
  const joined = await api<{ participantId:string; roomToken:string; snapshot:RoomSnapshotView }>("/api/v1/rooms/join", { method:"POST", headers:{ "content-type":"application/json", authorization:`Bearer ${guest.token}` }, body:JSON.stringify({ code:code.toUpperCase(), nickname }) });
  const credentials:RoomCredentials = { roomId:code.toUpperCase(), participantId:joined.participantId, roomToken:joined.roomToken, role:"participant", expiresAt:joined.snapshot.expiresAt };
  return { credentials, snapshot:joined.snapshot };
}

export function fetchRoomSnapshot(roomId:string, token:string, afterSequence=0) {
  return api<RoomSnapshotView>(`/api/v1/rooms/${roomId}/snapshot?afterSequence=${afterSequence}`, { headers:{ authorization:`Bearer ${token}` } });
}

export async function sendRoomEvent(credentials:RoomCredentials, snapshot:RoomSnapshotView, type:Exclude<SharedRoomEventType,"PARTICIPANT_JOINED"|"PARTICIPANT_DISCONNECTED"|"PARTICIPANT_RECONNECTED">, payload:Record<string,unknown> = {}) {
  const eventId=crypto.randomUUID();
  let current=snapshot;
  for (let attempt=0; attempt<2; attempt+=1) {
    try {
      await api(`/api/v1/rooms/${credentials.roomId}/events`, { method:"POST", headers:{ "content-type":"application/json", authorization:`Bearer ${credentials.roomToken}` }, body:JSON.stringify({ eventId, expectedRevision:current.revision, type, payload }) });
      return fetchRoomSnapshot(credentials.roomId, credentials.roomToken);
    } catch (error) {
      if (!(error instanceof ApiRequestError) || error.status!==409 || attempt===1) throw error;
      current=await fetchRoomSnapshot(credentials.roomId, credentials.roomToken);
    }
  }
  return current;
}

export async function reconnectRoom(credentials:RoomCredentials) {
  await api(`/api/v1/rooms/${credentials.roomId}/reconnect`, { method:"POST", headers:{ authorization:`Bearer ${credentials.roomToken}` } });
  return fetchRoomSnapshot(credentials.roomId, credentials.roomToken);
}
